import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { PeriodSelect, periodRange, previousRange, PERIODS } from "@/components/PeriodSelect";
import { fmtIST } from "@/lib/utils";
import { ClientCombobox } from "@/components/ClientCombobox";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { useChartColors } from "@/lib/useChartColors";
import { SocPollerBanner } from "@/components/SocPollerBanner";
import { ShieldWarning, Timer, CheckCircle, WarningOctagon, UserMinus, Detective, Gauge, Hourglass, Users, ArrowFatLinesUp, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const SEV_COLORS = { Critical: "#ef4444", High: "#f97316", Medium: "#f59e0b", Low: "#38bdf8" };

const fmtDelta = (d) => {
  const diff = Math.round((d.now - d.before) * 100) / 100;
  if (!diff) return { text: "no change", cls: "text-muted-foreground", Icon: Minus };
  const good = d.invert ? diff < 0 : diff > 0;
  const pct = d.before ? ` (${diff > 0 ? "+" : ""}${Math.round((diff / d.before) * 100)}%)` : "";
  return { text: `${diff > 0 ? "+" : ""}${diff}${pct}`, cls: good ? "text-rag-green-fg" : "text-rag-red-fg", Icon: diff > 0 ? TrendUp : TrendDown };
};

const Kpi = ({ label, value, hint, icon: Icon, tone = "", to, delta }) => {
  const nav = useNavigate();
  const testid = `soc-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const clickable = Boolean(to);
  const d = delta ? fmtDelta(delta) : null;
  return (
    <Card role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => nav(to) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(to); } } : undefined}
      title={clickable ? `Open ${label.toLowerCase()} cases` : undefined}
      className={`p-5 border border-border bg-card/60 rounded-sm transition-colors ${clickable ? "cursor-pointer hover:border-primary/60 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50" : ""}`}
      data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-2">{label}</div>
          <div className={`font-display text-3xl font-bold ${tone}`}>{value}</div>
          {hint && <div className="text-xs text-muted-foreground mt-1 font-mono">{hint}</div>}
          {d && <div className={`text-[11px] font-mono mt-1.5 flex items-center gap-1 whitespace-nowrap ${d.cls}`} data-testid={`${testid}-delta`} title={`Previous period: ${delta.before}`}><d.Icon size={12} weight="bold" /> {d.text} <span className="text-muted-foreground">vs prev</span></div>}
        </div>
        <div className="flex flex-col items-end gap-2">
          {Icon && <Icon size={20} className="text-muted-foreground" />}
        </div>
      </div>
    </Card>
  );
};

export default function SocDashboard() {
  const { user } = useAuth();
  const isLead = user && (user.role === "soc_lead" || user.role === "admin");
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);
  const [clients, setClients] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [params, setParams] = useSearchParams();
  const client = params.get("client") || "all";
  const myTeam = params.get("my_team") === "1";
  const period = params.get("period") || "all";
  const custom = { from: params.get("from") || "", to: params.get("to") || "" };
  const setParam = (patch) => {
    const p = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v && v !== "all" && v !== false) ? p.set(k, v === true ? "1" : v) : p.delete(k));
    setParams(p, { replace: true });
  };
  const setClient = (v) => setParam({ client: v });
  const setMyTeam = (fn) => setParam({ my_team: fn(myTeam) });
  const setPeriod = (v) => setParam({ period: v, ...(v !== "custom" ? { from: "", to: "" } : {}) });
  const setCustom = (c) => setParam({ from: c.from, to: c.to });
  const range = periodRange(period, custom);
  const prevRange = previousRange(range);
  const periodLabel = PERIODS.find(([k]) => k === period)?.[1] || "Till date";
  const delta = (key, invert = false) => prevRange && prev ? { now: data?.[key] ?? 0, before: prev[key] ?? 0, invert } : null;
  const chart = useChartColors();
  const viewer = user?.role === "soc_viewer";
  const [refreshedAt, setRefreshedAt] = useState(null);
  const casesLink = (extra = {}) => {
    if (viewer) return undefined;
    const p = new URLSearchParams();
    if (client && client !== "all") p.set("client", client);
    if (myTeam) p.set("my_team", "1");
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
    const qs = p.toString();
    return `/soc/cases${qs ? `?${qs}` : ""}`;
  };

  useEffect(() => { api.get("/client-registry?module=soc").then(r => setClients(r.data)).catch(() => {}); }, []);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!viewer) return;
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, [viewer]);
  useEffect(() => {
    const p = new URLSearchParams({ client });
    if (myTeam) p.set("my_team", "true");
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    api.get(`/soc/dashboard?${p.toString()}`).then(r => { setData(r.data); setRefreshedAt(new Date()); }).catch(() => {});
    if (prevRange) {
      p.set("date_from", prevRange.from); p.set("date_to", prevRange.to);
      api.get(`/soc/dashboard?${p.toString()}`).then(r => setPrev(r.data)).catch(() => setPrev(null));
    } else setPrev(null);
  }, [client, myTeam, range.from, range.to, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isLead) return;
    const p = new URLSearchParams({ client });
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    api.get(`/soc/metrics/analysts?${p.toString()}`).then(r => setAnalysts(r.data)).catch(() => {});
  }, [isLead, client, range.from, range.to]);

  if (!data) return <div className="p-6 text-muted-foreground">Loading SOC metrics…</div>;

  const sevData = Object.entries(data.by_severity || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v, fill: SEV_COLORS[k] }));
  const catData = Object.entries(data.by_category || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v })).sort((a, b) => b.value - a.value);
  const statusData = Object.entries(data.by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));

  return (
    <div className="p-6 space-y-6" data-testid="soc-dashboard-view">
      {!viewer && <SocPollerBanner isLead={isLead} />}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red mb-1">Security Operations</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">SOC Command Deck</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Period</span>
          <PeriodSelect value={period} onChange={setPeriod} custom={custom} onCustomChange={setCustom} testid="soc-period-select" />
          {prevRange && <span className="text-[10px] font-mono text-muted-foreground" data-testid="soc-period-compare" title="Deltas on the tiles compare against this window">vs {prevRange.from} → {prevRange.to}</span>}
          {viewer ? (
            <span className="h-9 px-3 rounded-sm text-[10px] font-mono uppercase tracking-wider border border-rag-green-fg/40 text-rag-green-fg flex items-center gap-1.5" data-testid="soc-live-indicator">
              <span className="h-1.5 w-1.5 rounded-full bg-rag-green animate-pulse" /> Live · {refreshedAt ? refreshedAt.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—"}
            </span>
          ) : (
          <button
            data-testid="soc-dash-my-team"
            onClick={() => setMyTeam(v => !v)}
            className={`h-9 px-3 rounded-sm text-xs font-mono uppercase tracking-wider border transition-colors flex items-center gap-1.5 ${myTeam ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
            title="Show only my team's cases"
          >
            <Users size={14} /> My Team
          </button>
          )}
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Client</span>
          <ClientCombobox value={client} onChange={setClient} clients={clients} className="w-56" testid="soc-client-selector" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Kpi label="Total" value={data.total} icon={ShieldWarning} to={casesLink()} delta={delta("total", true)} />
        <Kpi label="Open" value={data.open} icon={WarningOctagon} tone="text-rag-amber-fg" to={casesLink({ status: "open" })} delta={delta("open", true)} />
        <Kpi label="Closed" value={data.closed} icon={CheckCircle} tone="text-rag-green-fg" to={casesLink({ status: "closed_any" })} delta={delta("closed")} />
        <Kpi label="Unassigned" value={data.unassigned} icon={UserMinus} to={casesLink({ assignee: "unassigned" })} delta={delta("unassigned", true)} />
        <Kpi label="SLA Due Soon" value={data.sla_due_soon || 0} icon={Hourglass} tone={data.sla_due_soon > 0 ? "text-rag-amber-fg" : ""} hint="Approaching, not breached" to={casesLink({ sla: "due_soon" })} delta={delta("sla_due_soon", true)} />
        <Kpi label="SLA Breached" value={data.resolution_breached} icon={WarningOctagon} tone={data.resolution_breached > 0 ? "text-rag-red-fg" : ""} to={casesLink({ sla: "breached" })} delta={delta("resolution_breached", true)} />
        <Kpi label="True Positives" value={data.true_positive} icon={Detective} to={casesLink({ classification: "True Positive" })} delta={delta("true_positive")} />
        <Kpi label="TP:FP Ratio" value={data.tp_fp_ratio} icon={Gauge} hint={`${data.true_positive} TP / ${data.false_positive} FP`} to={casesLink({ classification: "False Positive" })} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label="MTTD (hrs)" value={data.mttd_hours} icon={Timer} hint="Detect → logged" delta={delta("mttd_hours", true)} />
        <Kpi label="MTTA (hrs)" value={data.mtta_hours} icon={Timer} hint="Created → first response" delta={delta("mtta_hours", true)} />
        <Kpi label="MTTR (hrs)" value={data.mttr_hours} icon={Timer} hint="Created → resolved" delta={delta("mttr_hours", true)} />
      </div>

      <Card className="p-5 border border-border bg-card/60 rounded-sm" data-testid="soc-escalation-metrics">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono flex items-center gap-1.5"><ArrowFatLinesUp size={13} className="text-rag-amber-fg" /> Tier 2 escalations</div>
            <div className="text-xs text-muted-foreground mt-0.5">Hand-offs from L1 triage — a high rate or slow time-to-escalate points to triage gaps.</div>
          </div>
          {!viewer && <Link to="/soc/cases?escalated=1" data-testid="soc-esc-open-queue" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline">Open Tier 2 queue →</Link>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Hand-offs</div><div className="font-display text-3xl font-bold" data-testid="soc-esc-total">{data.tier2_escalations || 0}</div><div className="text-xs text-muted-foreground font-mono">all time in view</div></div>
          <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Still open</div><div className={`font-display text-3xl font-bold ${data.tier2_open > 0 ? "text-rag-amber-fg" : ""}`} data-testid="soc-esc-open">{data.tier2_open || 0}</div><div className="text-xs text-muted-foreground font-mono">awaiting Tier 2</div></div>
          <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Escalation rate</div><div className={`font-display text-3xl font-bold ${data.tier2_rate_pct >= 40 ? "text-rag-red-fg" : data.tier2_rate_pct >= 20 ? "text-rag-amber-fg" : ""}`} data-testid="soc-esc-rate">{data.tier2_rate_pct || 0}%</div><div className="text-xs text-muted-foreground font-mono">of all cases</div></div>
          <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Avg time-to-escalate</div><div className={`font-display text-3xl font-bold ${data.mtte_hours >= 8 ? "text-rag-red-fg" : data.mtte_hours >= 2 ? "text-rag-amber-fg" : ""}`} data-testid="soc-esc-mtte">{data.mtte_hours || 0}<span className="text-base font-mono text-muted-foreground ml-1">hrs</span></div><div className="text-xs text-muted-foreground font-mono">created → handed off</div></div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4">Cases by Severity</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sevData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {sevData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip contentStyle={chart.tooltip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4">Cases by Status</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={{ fill: chart.axis, fontSize: 10 }} />
                <YAxis tick={{ fill: chart.axis, fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={chart.tooltip} />
                <Bar dataKey="value" fill="#38bdf8" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4">Top Categories</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={catData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis type="number" tick={{ fill: chart.axis, fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: chart.axis, fontSize: 10 }} width={120} />
                <Tooltip contentStyle={chart.tooltip} />
                <Bar dataKey="value" fill="#a78bfa" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4">Cases Over Time (30d)</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="date" tick={{ fill: chart.axis, fontSize: 9 }} tickFormatter={(d) => d.slice(5)} interval={4} />
                <YAxis tick={{ fill: chart.axis, fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={chart.tooltip} />
                <Line type="monotone" dataKey="count" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {isLead && (
        <Card className="p-5 border-border rounded-sm" data-testid="soc-analyst-metrics">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Users size={14} /> Analyst Performance</div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="soc-analyst-period">{periodLabel}{range.from && ` · ${range.from} → ${range.to || "today"}`}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2">Analyst</th>
                  <th className="text-right py-2">Assigned</th>
                  <th className="text-right py-2">Open</th>
                  <th className="text-right py-2">Closed</th>
                  <th className="text-right py-2">Avg Resolution (hrs)</th>
                </tr>
              </thead>
              <tbody>
                {analysts.map(a => (
                  <tr key={a.assignee_id} data-testid={`soc-analyst-row-${a.assignee_id}`} className="border-b border-border/50">
                    <td className="py-2">{a.assignee_name}</td>
                    <td className="py-2 text-right font-mono">{a.total_assigned}</td>
                    <td className="py-2 text-right font-mono text-rag-amber-fg">{a.open_assigned}</td>
                    <td className="py-2 text-right font-mono text-rag-green-fg">{a.closed}</td>
                    <td className="py-2 text-right font-mono">{a.avg_resolution_hours}</td>
                  </tr>
                ))}
                {analysts.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground text-xs">No assigned cases in this range.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <MitreCoveragePanel client={client} range={range} tick={tick} lead={isLead} />
      <AttackHeatmap client={client} range={range} tick={tick} />
    </div>
  );
}

function AttackHeatmap({ client, range, tick }) {
  const [hm, setHm] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams({ client });
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    api.get(`/soc/mitre/heatmap?${p.toString()}`).then(r => setHm(r.data)).catch(() => {});
  }, [client, range.from, range.to, tick]);
  if (!hm) return null;
  const shade = (c) => {
    const intensity = hm.max ? c / hm.max : 0;
    return { backgroundColor: `rgba(239,68,68,${0.15 + intensity * 0.75})`, color: intensity > 0.55 ? "#fff" : "inherit" };
  };
  return (
    <Card className="p-5 border-border rounded-sm" data-testid="soc-attack-heatmap">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">ATT&CK Heatmap ({hm.total_tagged} tagged)</div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="soc-heatmap-period">{range.from ? `${range.from} → ${range.to || "today"}` : "Till date"}</span>
      </div>
      {hm.tactics.length === 0 && <div className="text-xs text-muted-foreground">No ATT&CK techniques tagged in this range.</div>}
      <div className="space-y-3">
        {hm.tactics.map(row => (
          <div key={row.tactic} data-testid={`soc-heatmap-tactic-${row.tactic}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{row.tactic.replace(/-/g, " ")}</div>
            <div className="flex flex-wrap gap-1.5">
              {row.techniques.map(t => (
                <span key={t.id} title={`${t.id} ${t.name}: ${t.count}`} style={shade(t.count)}
                  className="text-[10px] font-mono px-2 py-1 rounded-sm border border-border">
                  {t.id} · {t.count}
                </span>
              ))}
            </div>
          </div>
        ))}
        {hm.unmapped_total > 0 && (
          <div data-testid="soc-heatmap-unmapped">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Unmapped closed true positives <span className="normal-case tracking-normal">· excluded from technique counts</span></div>
            <div className="flex flex-wrap gap-1.5">
              {[["tactic_only", "Tactic only"], ["not_mappable", "Not mappable"], ["overridden", "Overridden"], ["none", "No mapping (grandfathered)"]].filter(([k]) => hm.unmapped[k] > 0).map(([k, l]) => (
                <span key={k} data-testid={`soc-heatmap-unmapped-${k}`} className="text-[10px] font-mono px-2 py-1 rounded-sm border border-dashed border-border text-muted-foreground">{l} · {hm.unmapped[k]}</span>
              ))}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground mt-1.5">Closed TPs {hm.closed_tp} = mapped {hm.closed_tp_mapped} + unmapped {hm.unmapped_total}</div>
          </div>
        )}
      </div>
    </Card>
  );
}

const COV = [["mapped", "Technique mapped", "bg-rag-green"], ["tactic_only", "Tactic only", "bg-rag-amber"], ["not_mappable", "Not mappable", "bg-muted-foreground"], ["overridden", "Overridden", "bg-rag-red"], ["none", "No mapping (pre-rule)", "bg-border"]];

function MitreCoveragePanel({ client, range, tick, lead }) {
  const [cov, setCov] = useState(null);
  const [review, setReview] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const qs = () => { const p = new URLSearchParams({ client }); if (range.from) p.set("date_from", range.from); if (range.to) p.set("date_to", range.to); return p.toString(); };
  useEffect(() => { api.get(`/soc/mitre/coverage?${qs()}`).then(r => setCov(r.data)).catch(() => {}); }, [client, range.from, range.to, tick]); // eslint-disable-line
  useEffect(() => { if (lead && showReview) api.get(`/soc/mitre/review?${qs()}`).then(r => setReview(r.data)).catch(() => {}); }, [lead, showReview, client, range.from, range.to, tick]); // eslint-disable-line
  if (!cov) return null;
  return (
    <Card className="p-5 border-border rounded-sm" data-testid="soc-mitre-coverage">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">True Positive MITRE Coverage</div>
          <div className="text-xs text-muted-foreground mt-0.5">{cov.closed_tp} closed true positive(s) in range{cov.gate?.enabled ? "" : " · closure gate is OFF"}</div>
        </div>
        {lead && <button data-testid="soc-mitre-review-toggle" onClick={() => setShowReview(v => !v)} className="text-xs font-mono uppercase tracking-widest text-primary hover:underline">{showReview ? "Hide review list" : "Review not-mappable / overridden →"}</button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {COV.filter(([k]) => k !== "none" || cov.counts.none > 0).map(([k, label, bar]) => (
          <div key={k} data-testid={`soc-cov-${k}`} className="border border-border rounded-sm p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-display text-2xl font-bold mt-1">{cov.pct[k]}%</div>
            <div className="text-[10px] font-mono text-muted-foreground">{cov.counts[k]} case(s)</div>
            <div className="h-1 bg-accent rounded-sm mt-2 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${cov.pct[k]}%` }} /></div>
          </div>
        ))}
      </div>
      {lead && showReview && (
        <div className="mt-4 border border-border rounded-sm overflow-hidden" data-testid="soc-mitre-review-list">
          <table className="w-full text-xs">
            <thead className="bg-accent/30 text-[10px] font-mono uppercase tracking-widest text-muted-foreground"><tr><th className="text-left px-3 py-2">Case</th><th className="text-left px-3 py-2">State</th><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Closed</th></tr></thead>
            <tbody>
              {(review || []).map(r => (
                <tr key={r.id} data-testid={`soc-mitre-review-${r.number}`} className="border-t border-border hover:bg-accent/20 cursor-pointer" onClick={() => window.location.assign(`/soc/cases/${r.id}`)}>
                  <td className="px-3 py-2"><span className="font-mono text-primary">{r.number}</span> <span className="text-muted-foreground">{r.title}</span></td>
                  <td className="px-3 py-2"><span className={`font-mono uppercase text-[10px] px-1.5 py-0.5 border ${r.state === "overridden" ? "border-rag-red/40 text-rag-red-fg" : "border-border text-muted-foreground"}`}>{r.state.replace("_", " ")}</span></td>
                  <td className="px-3 py-2 max-w-md">{r.reason}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.by || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground font-mono" title={fmtIST(r.closed_at)}>{fmtIST(r.closed_at)}</td>
                </tr>
              ))}
              {review && review.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Nothing to review — every closed true positive in range has a technique or tactic.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
